import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ClientService } from '../../core/api/client.service';
import { environment } from '../../../environments/environment';
import { Provider } from '@supabase/supabase-js';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    MatFormFieldModule, MatInputModule, MatIconModule,
    MatButtonModule, FormsModule, ReactiveFormsModule,
    RouterModule, TranslateModule
  ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private client = inject(ClientService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);

  loginForm: FormGroup
  showMessage = false
  errorMessage: string | null = null
  submitting = false

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    const error = this.route.snapshot.queryParamMap.get('error');
    if (error) {
      this.showMessage = true;
      this.errorMessage = this.translate.instant('auth.errors.oauth_failed');
    }
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid || this.submitting) return
    this.showMessage = false
    this.submitting = true

    const { email, password } = this.loginForm.value;

    this.client.auth.login(email, password).subscribe({
      next: async ({ session }) => {
        if (!session) {
          this.failLogin();
          return;
        }

        try {
          const { error } = await this.client.supabase.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          });

          if (error) {
            this.failLogin();
            return;
          }

          this.router.navigate(['/dashboard']);
        } catch {
          this.failLogin();
        }
      },
      error: () => this.failLogin(),
    });
  }

  private failLogin(): void {
    this.submitting = false;
    this.showMessage = true;
    this.errorMessage = this.translate.instant('auth.errors.login_failed');
  }

  async loginOauth(provider: Provider): Promise<void> {
    if (this.submitting) return;
    this.submitting = true;

    const { error } = await this.client.supabase.auth.signInWithOAuth({
      provider, options: {
        redirectTo: `${environment.url}/redirect`
      }
    });

    if (error) {
      this.submitting = false;
      this.showMessage = true;
      this.errorMessage = this.translate.instant('auth.errors.oauth_failed');
    }
  }
}
